import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  criarCheckoutPublico,
  fetchCheckoutDetalhes,
  validarCupomEvento,
  verificarCadastroCpf as verificarCadastroCpfApi,
  verificarStatusPagamento,
} from '../../api/checkout';
import { copiarTexto } from '../../shared/utils/copiarTexto';
import {
  aplicarMascara,
  ESTADO_INICIAL,
  montarPayload,
  validarCPF,
} from './CheckoutFormModel';

export const useCheckoutViewModel = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [eventoInfo, setEventoInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [dadosBase, setDadosBase] = useState(ESTADO_INICIAL.dadosBase);
  const [dadosCartao, setDadosCartao] = useState(ESTADO_INICIAL.cartao);

  const [horarioSelecionadoId, setHorarioSelecionadoId] = useState(null);
  const [selectedDayNumero, setSelectedDayNumero] = useState(null);

  const planoParam = searchParams.get('plano');
  const planoInicial = [
    'assinatura',
    'ingresso_social',
    'encontro_avulso',
  ].includes(planoParam)
    ? planoParam
    : null;

  const [planoSelecionado, setPlanoSelecionado] = useState(planoInicial);
  const [metodoPagamento, setMetodoPagamento] = useState(
    planoInicial === 'assinatura' ? 'cartao' : 'pix',
  );

  // Se veio por URL com plano=assinatura, pré-seleciona cartão
  useEffect(() => {
    if (planoSelecionado === 'assinatura' && planoParam === 'assinatura') {
      setMetodoPagamento('cartao');
    }
  }, []);

  // Detecta se é um evento especial (sem múltiplos planos)
  const [isEventoEspecial, setIsEventoEspecial] = useState(false);
  // Cupom já validado: { codigo, descontoCentavos }. O desconto vale para o
  // plano em que foi validado, então trocar de plano descarta o cupom.
  const [cupom, setCupom] = useState(null);
  const selecionarPlano = (plano) => {
    setPlanoSelecionado(plano);
    setCupom(null);
  };
  const validarCupom = (codigo) =>
    validarCupomEvento({ codigo, slug, plano: planoSelecionado });

  // Pula passo de seleção de plano se:
  // 1. Já veio pré-selecionado via URL, ou
  // 2. For um evento especial (etapa será 'evento_especial' em vez de 'plano')
  const [etapa, setEtapa] = useState(planoParam ? 'dados' : 'plano');
  const [paymentResult, setPaymentResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(1800);
  const [erros, setErros] = useState({});
  const [mensagemStatus, setMensagemStatus] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);

  const [statusUsuario, setStatusUsuario] = useState({
    verificado: false,
    situacao: 'limpo',
  });

  const [termoAceito, setTermoAceito] = useState(false);
  const [termosCondicoesAceitos, setTermosCondicoesAceitos] = useState(false);
  const [socialDocumentUrl, setSocialDocumentUrl] = useState('');
  const [socialDocumentNote, setSocialDocumentNote] = useState('');

  // PIX countdown timer — pauses when payment is already confirmed
  useEffect(() => {
    if (etapa !== 'sucesso' || metodoPagamento !== 'pix' || isConfirmed) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setEtapa('expirado');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [etapa, metodoPagamento, isConfirmed]);

  // PIX payment polling (15s interval, max 20 attempts)
  useEffect(() => {
    if (
      isConfirmed ||
      etapa !== 'sucesso' ||
      metodoPagamento !== 'pix' ||
      !paymentResult
    )
      return;

    const targetId =
      paymentResult.registrationId ||
      paymentResult.id ||
      paymentResult.inscricaoId;
    if (!targetId) return;

    let attempts = 0;

    const interval = setInterval(async () => {
      attempts++;

      if (attempts >= 20) {
        clearInterval(interval);
        setMensagemStatus(
          'Seu pagamento pode estar sendo processado. Se o valor já foi debitado, atualize a página para confirmar.',
        );
        return;
      }

      try {
        const data = await verificarStatusPagamento(targetId);

        if (data.paid === true || data.status === 'CONFIRMED') {
          clearInterval(interval);
          setIsConfirmed(true);
          setPaymentResult((prev) => ({
            ...prev,
            pixQrCode: null,
            pixCopiaECola: null,
          }));
        }
      } catch (err) {
        console.error('[POLLING ERROR]', err);
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [etapa, metodoPagamento, paymentResult, isConfirmed]);

  // Load event details
  useEffect(() => {
    if (!slug) return;
    setLoading(true);

    fetchCheckoutDetalhes(slug)
      .then((detalhes) => {
        const horarios = detalhes?.horariosDisponiveis || [];
        const normalized = { ...detalhes, horariosDisponiveis: horarios };
        setEventoInfo(normalized);

        // Detecta se é um evento especial (sem múltiplos planos)
        // Evento especial = não tem valorMembroCentavos ou valorSocialCentavos
        const temMultiplosPlanos =
          (normalized.valorMembroCentavos &&
            normalized.valorMembroCentavos > 0) ||
          (normalized.valorSocialCentavos &&
            normalized.valorSocialCentavos > 0);

        if (!temMultiplosPlanos && !planoParam) {
          // É um evento especial, pré-seleciona o único plano disponível
          setIsEventoEspecial(true);
          setPlanoSelecionado('encontro_avulso');
          setEtapa('evento_especial');
        }

        const primeiroDisponivel = normalized.horariosDisponiveis?.find(
          (h) => !h.esgotado,
        );
        if (primeiroDisponivel) {
          setHorarioSelecionadoId(primeiroDisponivel.id);
          setSelectedDayNumero(primeiroDisponivel.numero);
        }

        // Evento somente PIX — força o método, mesmo que o usuário tivesse
        // vindo com plano=assinatura (que pré-seleciona cartão).
        if (normalized.somentePix) {
          setMetodoPagamento('pix');
        }
      })
      .catch((e) => {
        console.error('[CHECKOUT-VM] Erro ao buscar detalhes:', e);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  // Validates only personal data fields (used in step 1)
  const validarDadosPessoais = () => {
    const errosLocais = {};

    if (!dadosBase.nome?.trim())
      errosLocais.nome = 'O nome completo é obrigatório';

    if (!dadosBase.email?.trim()) {
      errosLocais.email = 'O e-mail é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dadosBase.email.trim())) {
      errosLocais.email = 'Insira um e-mail válido';
    }

    const cpfDigits = dadosBase.cpf?.replace(/\D/g, '') || '';
    if (!cpfDigits) {
      errosLocais.cpf = 'O CPF é obrigatório';
    } else if (cpfDigits.length !== 11) {
      errosLocais.cpf = 'CPF incompleto (11 dígitos)';
    } else if (!validarCPF(cpfDigits)) {
      errosLocais.cpf = 'CPF inválido. Verifique os números digitados.';
    }

    const foneDigits = dadosBase.whatsapp?.replace(/\D/g, '') || '';
    if (!foneDigits) {
      errosLocais.whatsapp = 'O WhatsApp é obrigatório';
    } else if (foneDigits.length < 10 || foneDigits.length > 11) {
      errosLocais.whatsapp = 'Número de WhatsApp inválido';
    }

    return errosLocais;
  };

  // Validates all fields including session and card (used in step 2)
  const validarCamposFormulario = () => {
    const errosLocais = { ...validarDadosPessoais() };

    if (!horarioSelecionadoId)
      errosLocais.horario =
        'A escolha da sessão/horário preferencial é obrigatória';

    if (metodoPagamento === 'cartao') {
      const numeroLimpo = dadosCartao.numero?.replace(/\s/g, '') || '';
      if (!numeroLimpo) {
        errosLocais.cartaoNumero = 'Número do cartão é obrigatório';
      } else if (numeroLimpo.length !== 16) {
        errosLocais.cartaoNumero = 'Número do cartão deve ter 16 dígitos';
      }

      if (!dadosCartao.nome?.trim())
        errosLocais.cartaoNome = 'Nome impresso é obrigatório';

      const [mes, ano] = (dadosCartao.validade || '').split('/');
      if (!dadosCartao.validade?.trim()) {
        errosLocais.cartaoValidade = 'Validade é obrigatória';
      } else if (
        !mes ||
        !ano ||
        mes.length !== 2 ||
        ano.length !== 2 ||
        isNaN(Number(mes)) ||
        isNaN(Number(ano))
      ) {
        errosLocais.cartaoValidade = 'Validade inválida (MM/AA)';
      } else {
        const mesNum = parseInt(mes, 10);
        const anoCompleto = 2000 + parseInt(ano, 10);
        const agora = new Date();
        const expirado =
          anoCompleto < agora.getFullYear() ||
          (anoCompleto === agora.getFullYear() &&
            mesNum < agora.getMonth() + 1);
        if (mesNum < 1 || mesNum > 12) {
          errosLocais.cartaoValidade = 'Mês inválido';
        } else if (expirado) {
          errosLocais.cartaoValidade = 'Cartão expirado';
        }
      }

      const cvvLimpo = dadosCartao.cvv?.trim() || '';
      if (!cvvLimpo) {
        errosLocais.cartaoCvv = 'Código CVV é obrigatório';
      } else if (cvvLimpo.length < 3 || cvvLimpo.length > 4) {
        errosLocais.cartaoCvv = 'CVV deve ter 3 ou 4 dígitos';
      }
    }

    setErros(errosLocais);
    return Object.keys(errosLocais).length === 0;
  };

  const handleInputChange = (campo, valor, objeto = 'dadosBase') => {
    if (erros[campo]) setErros((prev) => ({ ...prev, [campo]: null }));

    if (objeto === 'dadosBase') {
      setDadosBase((prev) => ({
        ...prev,
        [campo]: aplicarMascara(valor, campo),
      }));

      if (campo === 'cpf' && valor.replace(/\D/g, '').length === 11) {
        verificarSituacaoCpf(valor.replace(/\D/g, ''));
      }
    } else {
      setDadosCartao((prev) => ({
        ...prev,
        [campo]: aplicarMascara(valor, campo),
      }));
    }
  };

  const verificarSituacaoCpf = async (cpfLimpo) => {
    try {
      const situacao = await verificarCadastroCpfApi(cpfLimpo);

      if (situacao === 'inadimplente') {
        setErros((prev) => ({
          ...prev,
          cpf: '⚠️ Constamos uma fatura em aberto para este CPF. Regularize para reatar o acesso.',
        }));
        setStatusUsuario({ verificado: true, situacao: 'inadimplente' });
      } else if (situacao === 'ja_pago' && planoSelecionado === 'assinatura') {
        setErros((prev) => ({
          ...prev,
          cpf: '✨ Você já é assinante VIP ativa e sua mensalidade deste mês já está paga!',
        }));
        setStatusUsuario({ verificado: true, situacao: 'ja_pago' });
      } else {
        setStatusUsuario({ verificado: true, situacao: 'limpo' });
      }
    } catch {
      setStatusUsuario({ verificado: true, situacao: 'limpo' });
    }
  };

  const handleAvancarParaEscolhas = () => {
    const errosLocais = validarDadosPessoais();

    if (Object.keys(errosLocais).length > 0) {
      setErros(errosLocais);
      window.scrollTo({ top: 160, behavior: 'smooth' });
      return;
    }

    if (statusUsuario.situacao === 'inadimplente') {
      setErros((prev) => ({
        ...prev,
        cpf: 'Seu CPF possui faturas atrasadas no clube. Regularize para continuar.',
      }));
      return;
    }
    if (
      statusUsuario.situacao === 'ja_pago' &&
      planoSelecionado === 'assinatura'
    ) {
      setErros((prev) => ({
        ...prev,
        cpf: 'Sua assinatura já está ativa e paga para este mês!',
      }));
      return;
    }

    // Ingresso Social precisa enviar comprovante antes de escolher horário
    if (planoSelecionado === 'ingresso_social') {
      setEtapa('comprovante');
      return;
    }

    setEtapa('escolhas');
  };

  const handleFinalizarPedido = (confirm = false) => {
    if (!validarCamposFormulario()) {
      window.scrollTo({ top: 160, behavior: 'smooth' });
      return;
    }

    if (
      statusUsuario.situacao === 'inadimplente' ||
      statusUsuario.situacao === 'ja_pago'
    ) {
      setErros((prev) => ({
        ...prev,
        global:
          'Não é possível processar a transação devido ao status do seu CPF.',
      }));
      return;
    }

    if (!confirm) {
      setEtapa('pagamento');
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);
    setIsConfirmed(false);
    setErros((prev) => ({ ...prev, global: null }));

    const payload = montarPayload({
      slug,
      horarioId: horarioSelecionadoId,
      plano: planoSelecionado,
      metodoPagamento,
      dadosBase,
      cartao: dadosCartao,
      socialDocumentUrl,
      socialDocumentNote,
      socialDocumentMimeType: window.socialDocumentMimeType,
      termsAccepted: termosCondicoesAceitos,
      cupom: cupom?.codigo,
    });

    criarCheckoutPublico(payload)
      .then((data) => {
        // Inscrição já confirmada — não há cobrança nova
        if (data.alreadyRegistered) {
          setIsConfirmed(true);
          setMensagemStatus(
            data.message ||
              'Você já tem uma inscrição confirmada para este evento!',
          );
          setPaymentResult(data);
          setEtapa('sucesso');
          return;
        }

        // Pagamento pendente recente — retorna PIX já gerado sem criar novo
        if (data.alreadyPending) {
          if (data.pixQrCode && !data.pixQrCode.startsWith('data:image')) {
            data.pixQrCode = `data:image/png;base64,${data.pixQrCode}`;
          }
          setMensagemStatus(
            'Você já possui um pagamento em andamento para este evento.',
          );
          setTimeLeft(1800);
          setPaymentResult(data);
          setEtapa('sucesso');
          return;
        }

        // Ingresso Social — aguarda revisão do admin
        if (data.pendingApproval) {
          setPaymentResult(data);
          setEtapa('sucesso');
          return;
        }

        // Cartão aprovado sincronamente pelo gateway
        if (data.paid === true) {
          setIsConfirmed(true);
          setPaymentResult(data);
          setEtapa('sucesso');
          return;
        }

        // PIX gerado normalmente — valida antes de exibir
        if (metodoPagamento === 'pix') {
          if (!data.pixQrCode || !data.pixCopiaECola) {
            throw new Error('QR Code não foi gerado. Tente novamente.');
          }
          if (!data.pixQrCode.startsWith('data:image')) {
            data.pixQrCode = `data:image/png;base64,${data.pixQrCode}`;
          }
          setTimeLeft(1800);
        }

        setPaymentResult(data);
        setEtapa('sucesso');
      })
      .catch((err) => {
        console.error('[CHECKOUT] Erro no pagamento:', err);
        const mensagem =
          err.response?.data?.error ||
          (err.message?.includes('QR Code') ? err.message : null) ||
          'Não foi possível processar o pagamento. Tente novamente em instantes.';
        setErros((prev) => ({ ...prev, global: mensagem }));
        // O cupom pode ter esgotado ou vencido entre a prévia e o pagamento.
        if (mensagem.startsWith('Cupom')) setCupom(null);
        // Mantém na etapa de pagamento — o usuário não pode perder os dados do
        // cartão/método já preenchidos nem ser jogado de volta pra seleção de horário.
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const navigateHome = () => navigate('/');

  // null = ainda não tentou | true = copiou | false = falhou
  // Sem esse retorno a leitora clicava e nada mudava na tela, então não tinha
  // como saber se copiou. Quando falha, a tela revela o código para copiar na mão.
  const [pixCopiado, setPixCopiado] = useState(null);

  const copiarPixCopiaECola = async () => {
    const copiou = await copiarTexto(paymentResult?.pixCopiaECola);
    setPixCopiado(copiou);
    if (copiou) {
      setTimeout(() => setPixCopiado(null), 2500);
    }
  };

  const formatTimeRemaining = () => {
    const mins = Math.floor(timeLeft / 60);
    const secs = timeLeft % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return {
    eventoInfo,
    loading,
    isSubmitting,
    dadosBase,
    dadosCartao,
    handleInputChange,
    horarioSelecionadoId,
    setHorarioSelecionadoId,
    selectedDayNumero,
    setSelectedDayNumero,
    planoSelecionado,
    setPlanoSelecionado: selecionarPlano,
    cupom,
    setCupom,
    validarCupom,
    metodoPagamento,
    setMetodoPagamento,
    etapa,
    setEtapa,
    handleAvancarParaEscolhas,
    handleFinalizarPedido,
    paymentResult,
    copiarPixCopiaECola,
    pixCopiado,
    navigateHome,
    formattedTime: formatTimeRemaining(),
    erros,
    isConfirmed,
    statusUsuario,
    mensagemStatus,
    termoAceito,
    setTermoAceito,
    termosCondicoesAceitos,
    setTermosCondicoesAceitos,
    socialDocumentUrl,
    setSocialDocumentUrl,
    setSocialDocumentNote,
    isEventoEspecial,
  };
};
